// src/components/features/brands/BrandAssetManager.jsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Chip,
  CardContent,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  CloudUpload as UploadIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Label as TagIcon,
} from '@mui/icons-material';

// Import custom components
import { Card } from '../../design-system';
import { EmptyState } from '../../design-system/components/core';
import SecureAvatar from '../../components/ui/SecureAvatar';

// Asset categories from backend
const ASSET_CATEGORIES = [
  'product',
  'lifestyle',
  'campaign',
  'logo',
  'banner',
  'other'
];

const BrandAssetManager = ({ 
  brandId,
  assets = [],
  onUpload,
  onDelete,
  isLoading = false,
  canUpload = true,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  
  // Local state
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [assetFile, setAssetFile] = useState(null);
  const [assetMetadata, setAssetMetadata] = useState({
    category: 'other',
    tags: []
  });
  const [newTag, setNewTag] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  
  // All unique tags from assets
  const allTags = [...new Set(assets.flatMap(asset => asset.tags || []))];
  
  // Filter assets based on selected filters and search query
  const filteredAssets = assets.filter(asset => {
    const matchesCategory = !selectedCategory || asset.category === selectedCategory;
    const matchesTag = !selectedTag || (asset.tags && asset.tags.includes(selectedTag));
    const matchesSearch = !searchQuery || 
      (asset.originalname && asset.originalname.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesCategory && matchesTag && matchesSearch;
  });
  
  // Open filter menu
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  // Close filter menu
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    setSelectedCategory('');
    setSelectedTag('');
    setSearchQuery('');
    handleFilterClose();
  };
  
  // Handle file selection
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setAssetFile(e.target.files[0]);
      setAssetMetadata({
        ...assetMetadata,
        originalname: e.target.files[0].name
      });
      setUploadDialogOpen(true);
    }
  };
  
  // Add a new tag to the asset
  const handleAddTag = () => {
    if (newTag.trim() && !assetMetadata.tags.includes(newTag.trim())) {
      setAssetMetadata({
        ...assetMetadata,
        tags: [...assetMetadata.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };
  
  // Remove a tag from the asset
  const handleRemoveTag = (tagToRemove) => {
    setAssetMetadata({
      ...assetMetadata,
      tags: assetMetadata.tags.filter(tag => tag !== tagToRemove)
    });
  };
  
  // Handle upload dialog close
  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
    setAssetFile(null);
    setAssetMetadata({
      category: 'other',
      tags: []
    });
  };
  
  // Handle asset upload
  const handleUploadAsset = async () => {
    if (!assetFile) return;
    
    setIsUploading(true);
    try {
      await onUpload(brandId, assetFile, assetMetadata);
      handleUploadDialogClose();
    } catch (error) {
      console.error('Upload failed:', error);
      // Error handling would be done in the parent component
    } finally {
      setIsUploading(false);
    }
  };
  
  // Open asset view dialog
  const handleViewAsset = (asset) => {
    setSelectedAsset(asset);
    setViewDialogOpen(true);
  };
  
  // Get file extension
  const getFileExtension = (filename) => {
    return filename?.split('.').pop().toLowerCase() || '';
  };
  
  // Determine if asset is an image
  const isImageAsset = (asset) => {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'];
    return asset && (
      asset.fileType === 'image' || 
      imageExtensions.includes(getFileExtension(asset.originalname))
    );
  };
  
  // Copy asset URL to clipboard
  const copyAssetUrl = (url) => {
    navigator.clipboard.writeText(url);
    // Could add a toast notification here
  };
  
  return (
    <Box>
      {/* Search and Filter Bar */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <TextField
          placeholder="Search assets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<FilterIcon />} 
            onClick={handleFilterClick}
            endIcon={
              (selectedCategory || selectedTag) ? (
                <Chip 
                  label={(selectedCategory ? 1 : 0) + (selectedTag ? 1 : 0)} 
                  size="small" 
                  
                />
              ) : null
            }
          >
            Filter
          </Button>
          
          {canUpload && (
            <Button
              variant="contained"
              
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current.click()}
            >
              Upload
            </Button>
          )}
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </Box>
        
        {/* Filter Menu */}
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
          PaperProps={{
            sx: { width: 250, maxHeight: 500 }
          }}
        >
          <MenuItem>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Filter Assets
            </Typography>
          </MenuItem>
          
          <MenuItem>
            <FormControl fullWidth>
              <InputLabel id="category-filter-label">Category</InputLabel>
              <Select
                labelId="category-filter-label"
                id="category-filter"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label="Category"
                size="small"
                fullWidth
              >
                <MenuItem value="">All Categories</MenuItem>
                {ASSET_CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </MenuItem>
          
          {allTags.length > 0 && (
            <MenuItem>
              <FormControl fullWidth>
                <InputLabel id="tag-filter-label">Tag</InputLabel>
                <Select
                  labelId="tag-filter-label"
                  id="tag-filter"
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  label="Tag"
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">All Tags</MenuItem>
                  {allTags.map((tag) => (
                    <MenuItem key={tag} value={tag}>
                      {tag}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MenuItem>
          )}
          
          <MenuItem onClick={handleClearFilters}>
            <Typography >Clear All Filters</Typography>
          </MenuItem>
        </Menu>
      </Box>
      
      {/* Active Filters */}
      {(selectedCategory || selectedTag) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {selectedCategory && (
            <Chip 
              label={`Category: ${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}`}
              onDelete={() => setSelectedCategory('')}
              
              variant="outlined"
            />
          )}
          {selectedTag && (
            <Chip 
              label={`Tag: ${selectedTag}`}
              onDelete={() => setSelectedTag('')}
              
              variant="outlined"
              icon={<TagIcon fontSize="small" />}
            />
          )}
        </Box>
      )}
      
      {/* Assets Grid */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredAssets.length === 0 ? (
        <EmptyState
          title={searchQuery || selectedCategory || selectedTag ? "No matching assets found" : "No Assets Yet"}
          description={searchQuery || selectedCategory || selectedTag ? 
            "Try changing your search or filters" : 
            "Upload brand assets like images, videos, and documents"}
          icon={<UploadIcon sx={{ fontSize: 64 }} />}
          action={
            canUpload ? (
              <Button
                variant="contained"
                
                startIcon={<AddIcon />}
                onClick={() => fileInputRef.current.click()}
                magical={true}
              >
                Upload Assets
              </Button>
            ) : null
          }
        />
      ) : (
        <Grid container spacing={3}>
          {filteredAssets.map((asset) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={asset._id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[4],
                  }
                }}
                onClick={() => handleViewAsset(asset)}
              >
                {/* Asset Preview */}
                <Box sx={{ position: 'relative' }}>
                  {isImageAsset(asset) ? (
                    <SecureAvatar
                      src={asset}
                      alt={asset.originalname || 'Brand asset'}
                      sx={{ 
                        height: 180, 
                        width: '100%',
                        objectFit: 'cover',
                        bgcolor: alpha(theme.palette.divider, 0.1)
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 180,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(theme.palette.divider, 0.1),
                        p: 2,
                      }}
                    >
                      <Typography variant="h6" align="center" sx={{ wordBreak: 'break-word' }}>
                        {getFileExtension(asset.originalname).toUpperCase()}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Category badge */}
                  <Chip
                    label={asset.category?.charAt(0)?.toUpperCase() + asset.category?.slice(1) || 'Other'}
                    size="small"
                    sx={{ 
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      bgcolor: alpha(theme.palette.background.paper, 0.8),
                    }}
                  />
                </Box>
                
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" noWrap title={asset.originalname}>
                    {asset.originalname}
                  </Typography>
                  
                  {/* Tags */}
                  {asset.tags && asset.tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {asset.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            height: 20,
                            fontSize: '0.7rem',
                            '& .MuiChip-label': { px: 1 }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTag(tag);
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
                
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyAssetUrl(asset.url);
                    }}
                    title="Copy URL"
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    component="a"
                    href={asset.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    title="Download"
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  {canUpload && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Are you sure you want to delete this asset?')) {
                          onDelete(brandId, asset._id);
                        }
                      }}
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upload Brand Asset
          <IconButton
            aria-label="close"
            onClick={handleUploadDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected File:
            </Typography>
            <Typography>
              {assetFile?.name}
            </Typography>
          </Box>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="asset-category-label">Asset Category</InputLabel>
            <Select
              labelId="asset-category-label"
              id="asset-category"
              value={assetMetadata.category}
              onChange={(e) => setAssetMetadata({...assetMetadata, category: e.target.value})}
              label="Asset Category"
            >
              {ASSET_CATEGORIES.map((category) => (
                <MenuItem key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Typography variant="subtitle2" gutterBottom>
            Tags:
          </Typography>
          <Box sx={{ display: 'flex', mb: 2 }}>
            <TextField
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              placeholder="Add a tag"
              size="small"
              sx={{ flexGrow: 1, mr: 1 }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={handleAddTag}
              disabled={!newTag.trim()}
            >
              Add
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {assetMetadata.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => handleRemoveTag(tag)}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button 
            variant="contained"
            
            onClick={handleUploadAsset}
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size={20} /> : null}
          >
            {isUploading ? 'Uploading...' : 'Upload Asset'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Asset View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedAsset && (
          <>
            <DialogTitle>
              {selectedAsset.originalname}
              <IconButton
                aria-label="close"
                onClick={() => setViewDialogOpen(false)}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                {isImageAsset(selectedAsset) ? (
                  <img
                    src={selectedAsset.url}
                    alt={selectedAsset.originalname}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '60vh',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette.divider, 0.1),
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="h3">
                      {getFileExtension(selectedAsset.originalname).toUpperCase()}
                    </Typography>
                  </Box>
                )}
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Category:</Typography>
                  <Typography>
                    {selectedAsset.category?.charAt(0)?.toUpperCase() + selectedAsset.category?.slice(1) || 'Other'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">File Type:</Typography>
                  <Typography>
                    {selectedAsset.fileType || getFileExtension(selectedAsset.originalname).toUpperCase()}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">URL:</Typography>
                  <TextField
                    value={selectedAsset.url}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            edge="end"
                            onClick={() => copyAssetUrl(selectedAsset.url)}
                            title="Copy URL"
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                {selectedAsset.tags && selectedAsset.tags.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Tags:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedAsset.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button 
                component="a"
                href={selectedAsset.url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<DownloadIcon />}
              >
                Download
              </Button>
              {canUpload && (
                <Button 
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this asset?')) {
                      onDelete(brandId, selectedAsset._id);
                      setViewDialogOpen(false);
                    }
                  }}
                >
                  Delete
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default BrandAssetManager;